import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import HomeView from '../views/HomeView.vue';
import AccessDeniedView from '../views/AccessDenied.vue';
import KeycloakService, {
  getSavedAccessToken,
  getTokenFromUrl,
} from '@/services/keycloakService';
import { useTokenStore } from '@/store/tokenStore';
import { UseStoreUser } from '@/store/userStore';
import {
  hasHubRoleFromPayload,
  parseAccessTokenPayload,
  type AccessTokenPayload,
} from '@/utils/token';

const ACCESS_DENIED_NAME = 'access-denied';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
  },
  {
    path: '/acces-refuse',
    name: ACCESS_DENIED_NAME,
    component: AccessDeniedView,
    meta: { accessDenied: true },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

/** Get access token payload. Tries tokenParsed first, then saved/window/store token and parse. */
function getTokenPayload(): AccessTokenPayload | null {
  const parsed = KeycloakService.currentInstance?.tokenParsed;
  // Use full tokenParsed whenever present (same source as loadUserRolesFromToken).
  // Requiring only `'resource_access' in parsed` skipped some Keycloak payloads.
  if (parsed && typeof parsed === 'object') {
    return parsed as AccessTokenPayload;
  }
  const rawToken: string | undefined =
    getSavedAccessToken()?.trim() ||
    useTokenStore().userAuth?.access_token?.trim() ||
    KeycloakService.token?.()?.trim() ||
    useTokenStore().token?.trim() ||
    getTokenFromUrl()?.trim() ||
    undefined;
  if (!rawToken) return null;
  return parseAccessTokenPayload(rawToken);
}

router.beforeEach((to, _from, next) => {
  const isAccessDeniedRoute =
    to.name === ACCESS_DENIED_NAME || Boolean(to.meta.accessDenied);
  if (isAccessDeniedRoute) {
    const payload = getTokenPayload();
    const userStore = UseStoreUser();
    const hasHubViaJwt =
      payload !== null && hasHubRoleFromPayload(payload);
    if (hasHubViaJwt || userStore.hasAnyHubRole) {
      next({ name: 'home', replace: true });
      return;
    }
    next();
    return;
  }
  const payload = getTokenPayload();
  if (payload !== null && !hasHubRoleFromPayload(payload)) {
    next({ name: ACCESS_DENIED_NAME });
    return;
  }
  next();
});

export default router;
